import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { FlexRow, Button, Spacer } from "../../components";
import { ColorsCodeType } from "../../typings";
import { Add } from "@mui/icons-material";
import CreateUserModal from "./CreateUserModal";
import { USER_INITIAL_VALUES } from "../../data";
import SearchBar from "../../components/atoms/SearchBar/SearchBar";
import { ROLES } from "../../data/users";
import { UserRolesEnum } from "../../typings/users";

const Wrapper = styled(FlexRow)`
  width: 100%;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0px 15px 0px;
  gap: 16px;
`;

const LeftSection = styled("div")`
  display: flex;
  align-items: center;
  flex: 1;
`;

const RightSection = styled("div")`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const StyledPrimaryButton = styled(Button)`
  min-width: 0;
  min-height: 32px;
  padding: 0px 10px;
  border-radius: 5px;
  color: ${({ theme }) => theme.palette.colors.gray["900"]};
  white-space: nowrap;
`;

const StyledFormControl = styled(FormControl)`
  min-width: 150px;
`;

interface UserScreenHeaderProps {
  onSearch: (searchTerm: string) => void;
  onRoleChange: (role: UserRolesEnum | "") => void;
  selectedRole: UserRolesEnum | "";
}

const UserScreenHeader: React.FC<UserScreenHeaderProps> = ({
  onSearch,
  onRoleChange,
  selectedRole,
}) => {
  const theme = useTheme();
  const {
    palette: { colors },
  } = theme;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleRoleSelect = (event: SelectChangeEvent<string>) => {
    onRoleChange(event.target.value as UserRolesEnum | "");
  };

  return (
    <Wrapper>
      <LeftSection>
        <Typography
          variant="h4"
          gutterBottom
          component="div"
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            marginBottom: 0,
            marginRight: 2,
          }}
        >
          Users Dashboard
        </Typography>
      </LeftSection>

      <RightSection>
        {/* Role Filter */}
        <StyledFormControl size="small">
          <Select
            value={selectedRole}
            onChange={handleRoleSelect}
            displayEmpty
            sx={{
              borderRadius: "8px",
              fontSize: "0.875rem",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.dark,
              },
            }}
          >
            <MenuItem value="">All Roles</MenuItem>
            {ROLES.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>

        <SearchBar onSearch={onSearch} placeholder="Search users..." />

        <StyledPrimaryButton
          buttonColor={colors["yellow"]["300"] as ColorsCodeType}
          onClick={handleOpenModal}
        >
          <Add fontSize="small" />
          <Spacer horizontal={0.3} />
          {"Create User"}
        </StyledPrimaryButton>
      </RightSection>

      <CreateUserModal
        open={isModalOpen}
        onClose={handleCloseModal}
        user={USER_INITIAL_VALUES}
      />
    </Wrapper>
  );
};

export default UserScreenHeader;
